import './general-info.scss';

import { useContext } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import {
  Autocomplete,
  DatePickerField,
  Input,
  Select
} from '@/components/Shared/ui';

import { CreateTabContext } from '../../LlpOrder';
import type { CreateLeasingProps } from '../../widgets/create-tab';
import { useInitialData } from './hooks';

const GeneralInfo: React.FC = () => {
  const { isFieldsFrozen } = useContext(CreateTabContext);

  const {
    control,
    register,
    setValue,
    getValues,
    formState: { errors }
  } = useFormContext<CreateLeasingProps>();

  const { mainActivities, countries, propertyTypes, registrationPlaces } =
    useInitialData({
      getValues,
      setValue
    });

  return (
    <div className="general-info">
      <Controller
        control={control}
        name="iin"
        rules={{
          required: 'Пожалуйста, введите данные',
          minLength: {
            value: 12,
            message: 'Поле должно содержать 12 символов'
          }
        }}
        render={({ field }) => (
          <Input
            {...field}
            type="tel"
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              if (value.length > 12) return;
              field.onChange(value);
            }}
            label="ИИН/БИН"
            isRequired
            error={errors.iin?.message?.toString() || ''}
            isFrozen={isFieldsFrozen}
          />
        )}
      />
      <Controller
        control={control}
        name="companyName"
        rules={{
          required: 'Пожалуйста, введите данные',
          minLength: { value: 1, message: 'Пожалуйста, введите данные' }
        }}
        render={({ field }) => (
          <Input
            {...field}
            label="Наименование компании"
            error={errors.companyName?.message?.toString() || ''}
            isFrozen={isFieldsFrozen}
            isRequired
          />
        )}
      />
      <Input
        {...register('bankDetails')}
        label="Банковские реквизиты"
        isFrozen={isFieldsFrozen}
      />

      {/* --------------------------------------- */}

      <Controller
        control={control}
        name="oked"
        rules={{
          required: 'Пожалуйста, введите данные',
          minLength: { value: 1, message: 'Пожалуйста, введите данные' }
        }}
        render={({ field }) => (
          <Input
            {...field}
            type="tel"
            onChange={(e) => {
              const value = e.target.value.replace(/\s/g, '');
              if (value.match(/\D/) || value.length > 5) return;
              field.onChange(value);
            }}
            label="ОКЭД"
            isRequired
            error={errors.oked?.message?.toString() || ''}
            isFrozen={isFieldsFrozen}
          />
        )}
      />
      <Controller
        control={control}
        name="jobType"
        rules={{
          required: 'Пожалуйста, введите данные',
          minLength: { value: 1, message: 'Пожалуйста, введите данные' }
        }}
        render={({ field }) => (
          <Select
            {...field}
            label="Вид деятельности"
            selectedValue={mainActivities.find(
              (item) => item.value === field.value
            )}
            onChange={(value) => {
              field.onChange(value.value);
            }}
            options={mainActivities}
            isRequired
            error={errors.jobType?.message?.toString() || ''}
            isFrozen={isFieldsFrozen}
          />
        )}
      />
      <Controller
        control={control}
        name="country"
        rules={{
          required: 'Пожалуйста, введите данные',
          minLength: { value: 1, message: 'Пожалуйста, введите данные' }
        }}
        render={({ field }) => (
          <Autocomplete
            {...field}
            label="Страна регистрации"
            options={countries}
            isRequired
            error={errors.country?.message?.toString() || ''}
            autoComplete="off"
            isFrozen={isFieldsFrozen}
          />
        )}
      />

      {/* --------------------------------------- */}

      <Controller
        control={control}
        name="legalAddress"
        rules={{
          required: 'Пожалуйста, введите данные',
          minLength: { value: 1, message: 'Пожалуйста, введите данные' }
        }}
        render={({ field }) => (
          <Input
            {...field}
            label="Юридический адрес"
            isRequired
            error={errors.legalAddress?.message?.toString() || ''}
            isFrozen={isFieldsFrozen}
          />
        )}
      />
      <Controller
        control={control}
        name="typeOfProperty"
        rules={{
          required: 'Пожалуйста, введите данные',
          minLength: { value: 1, message: 'Пожалуйста, введите данные' }
        }}
        render={({ field }) => (
          <Select
            {...field}
            selectedValue={propertyTypes.find(
              (item) => item.value === field.value
            )}
            onChange={(value) => {
              field.onChange(value.value);
            }}
            label="Вид собственности"
            options={propertyTypes}
            isRequired
            error={errors.typeOfProperty?.message?.toString() || ''}
            isFrozen={isFieldsFrozen}
          />
        )}
      />
      <Controller
        control={control}
        name="registrationPlace"
        rules={{
          required: 'Пожалуйста, введите данные',
          minLength: { value: 1, message: 'Пожалуйста, введите данные' }
        }}
        render={({ field }) => (
          <Select
            {...field}
            selectedValue={registrationPlaces.find(
              (item) => item.value === field.value
            )}
            onChange={(value) => {
              field.onChange(value.value);
            }}
            label="Место гос. регистрации"
            options={registrationPlaces}
            isRequired
            error={errors.registrationPlace?.message?.toString() || ''}
            isFrozen={isFieldsFrozen}
          />
        )}
      />

      {/* --------------------------------------- */}

      <Controller
        control={control}
        name="registrationNumber"
        rules={{
          required: 'Пожалуйста, введите данные',
          minLength: { value: 1, message: 'Пожалуйста, введите данные' }
        }}
        render={({ field }) => (
          <Input
            {...field}
            type="tel"
            onChange={(e) => {
              const value = e.target.value.replace(/\s/g, '');
              if (value.match(/\D/)) return;
              field.onChange(value);
            }}
            label="№ Гос. регистрации"
            isRequired
            error={errors.registrationNumber?.message?.toString() || ''}
            isFrozen={isFieldsFrozen}
          />
        )}
      />
      <Controller
        control={control}
        name="registrationDate"
        rules={{
          required: 'Пожалуйста, введите данные',
          validate: (value) => {
            const shouldValidate = value.length === '01.01.2020'.length;
            if (!shouldValidate) return 'Неверный формат даты';
          }
        }}
        render={({ field }) => (
          <DatePickerField
            {...field}
            label="Дата гос. регистрации"
            isRequired
            error={errors.registrationDate?.message?.toString() || ''}
            disableFuture
            viewFormat="DD.MM.YYYY"
            isFrozen={isFieldsFrozen}
          />
        )}
      />
      <Controller
        control={control}
        name="startWork"
        rules={{
          required: 'Пожалуйста, введите данные',
          validate: (value) => {
            const shouldValidate = value.length === '01.01.2020'.length;
            if (!shouldValidate) return 'Неверный формат даты';
          }
        }}
        render={({ field }) => (
          <DatePickerField
            {...field}
            label="Дата начала работы"
            isRequired
            error={errors.startWork?.message?.toString() || ''}
            isFrozen={isFieldsFrozen}
          />
        )}
      />

      {/* --------------------------------------- */}

      <Input
        {...register('license')}
        label="Кем выдана лицензия"
        isFrozen={isFieldsFrozen}
      />
      <Input
        {...register('licenseDescription')}
        label="Сведения о лицензии"
        isFrozen={isFieldsFrozen}
      />
      <Controller
        control={control}
        name="licenseNumber"
        render={({ field }) => (
          <Input
            {...field}
            type="tel"
            onChange={(e) => {
              const value = e.target.value.replace(/\s/g, '');
              if (value.match(/\D/)) return;
              field.onChange(value);
            }}
            label="Номер лицензии"
            isFrozen={isFieldsFrozen}
          />
        )}
      />
    </div>
  );
};

export default GeneralInfo;
